// Data model for the Car Advisor wizard, all in Italian.
// Each choice includes an explanatory paragraph ("why choose this").

import {
  Building2,
  Route,
  Gauge,
  Car,
  CarFront,
  Truck,
  Sparkles,
  Crown,
  Fuel,
  Zap,
  Leaf,
  Droplet,
  Wind,
  Cog,
  Settings2,
  Shuffle,
  BadgeCheck,
  Wrench,
  Coins,
  Wallet,
  PiggyBank,
  Gem,
  MapPin,
  Timer,
  Plane,
  Mountain,
  DoorOpen,
  Users as UsersIcon,
  Sailboat,
  Bus,
  CarTaxiFront,
} from "lucide-react";

export const STEPS = [
  { id: 1, title: "Uso & Percorrenza", subtitle: "Dove e quanto guiderai" },
  { id: 2, title: "Conducente & Preferenze", subtitle: "Chi guida e come" },
  { id: 3, title: "Tipologia & Budget", subtitle: "Che tipo di auto vuoi" },
  { id: 4, title: "Ricerca avanzata (opzionale)", subtitle: "Affina la selezione — puoi saltare" },
];

// Advanced filters (all optional)
export const BODY_TYPES = [
  { value: "any",       title: "Indifferente" },
  { value: "city_car",  title: "City car" },
  { value: "sedan",     title: "Berlina" },
  { value: "wagon",     title: "Station wagon" },
  { value: "suv",       title: "SUV" },
  { value: "crossover", title: "Crossover" },
  { value: "coupe",     title: "Coupé" },
  { value: "cabrio",    title: "Cabrio / Spider" },
  { value: "mpv",       title: "Monovolume" },
  { value: "offroad",   title: "Fuoristrada" },
  { value: "pickup",    title: "Pick-up" },
  { value: "van",       title: "Van 7 posti" },
  { value: "furgone",   title: "Furgone" },
];

export const WHEEL_DRIVE_OPTIONS = [
  { value: "any", title: "Indifferente" },
  { value: "2wd", title: "Trazione singola (2WD)" },
  { value: "4wd", title: "Trazione integrale (4x4)" },
];

export const DOORS_OPTIONS = [
  { value: "any", title: "Indifferente" },
  { value: "3",   title: "3 porte" },
  { value: "5",   title: "5 porte" },
];

export const SEATS_OPTIONS = [
  { value: "any", title: "Indifferente" },
  { value: "2",   title: "2 posti" },
  { value: "4",   title: "4 posti" },
  { value: "5",   title: "5 posti" },
  { value: "7",   title: "7 posti" },
  { value: "9",   title: "9 posti" },
];

export const USAGE_TYPES = [
  {
    value: "city",
    title: "Città",
    icon: Building2,
    why:
      "Perfetto se ti muovi soprattutto in aree urbane trafficate. Punteremo su auto compatte, agili e con consumi bassi nel ciclo urbano — ideali ibride o elettriche.",
  },
  {
    value: "mixed",
    title: "Misto (extraurbano)",
    icon: Route,
    why:
      "Se alterni città e strade extraurbane, cerchiamo un compromesso tra maneggevolezza e comfort. Motorizzazioni versatili e cambio automatico spesso sono la scelta migliore.",
  },
  {
    value: "highway",
    title: "Autostrada",
    icon: Gauge,
    why:
      "Molte percorrenze autostradali richiedono comfort, stabilità e coppia ai bassi regimi. Diesel o mild-hybrid restano interessanti sopra i 20.000 km/anno.",
  },
];

export const CAR_SIZES = [
  {
    value: "small",
    title: "Piccola",
    icon: Car,
    why:
      "Segmento A/B: facile da parcheggiare e leggera sui consumi. Ottima come prima auto o per famiglie in cui è un'auto secondaria in città.",
  },
  {
    value: "medium",
    title: "Media",
    icon: CarFront,
    why:
      "Segmento C: il miglior equilibrio tra spazio, comfort e prezzo. Adatta sia in città che per viaggi occasionali con la famiglia.",
  },
  {
    value: "large",
    title: "Grande / SUV",
    icon: Truck,
    why:
      "Segmento D+ o SUV: spazio, comfort di viaggio e posizione di guida rialzata. Ideale per famiglie numerose o per chi fa molti viaggi.",
  },
];

export const CONDITIONS = [
  {
    value: "new",
    title: "Nuova",
    icon: Sparkles,
    why:
      "Garanzia estesa, tecnologia di ultima generazione e possibilità di configurarla. Costa di più ma azzera i pensieri di manutenzione nei primi anni.",
  },
  {
    value: "used",
    title: "Usata",
    icon: BadgeCheck,
    why:
      "Con lo stesso budget puoi salire di segmento o allestimento. Occhio a chilometraggio, storico tagliandi e garanzia del venditore.",
  },
];

export const TRANSMISSIONS = [
  {
    value: "manual",
    title: "Manuale",
    icon: Cog,
    why:
      "Più economico all'acquisto e in manutenzione. Preferito da chi ama il controllo diretto e fa poche ore nel traffico.",
  },
  {
    value: "automatic",
    title: "Automatico",
    icon: Settings2,
    why:
      "Molto più comodo nel traffico cittadino e nei lunghi viaggi. Costa di più ma riduce la fatica di guida in modo netto.",
  },
  {
    value: "any",
    title: "Indifferente",
    icon: Shuffle,
    why:
      "Ti mostreremo entrambe le opzioni: valuteremo il cambio in base al modello e all'uso più che a una preferenza fissa.",
  },
];

export const FUEL_TYPES = [
  {
    value: "petrol",
    title: "Benzina",
    icon: Fuel,
    why:
      "Ottima per chi percorre meno di 15.000 km/anno, soprattutto in città. Minor costo d'acquisto e manutenzione semplice.",
  },
  {
    value: "diesel",
    title: "Diesel",
    icon: Droplet,
    why:
      "Ha ancora senso oltre i 20.000 km/anno con molta autostrada. Attenzione ai blocchi Euro nelle ZTL delle grandi città.",
  },
  {
    value: "hybrid",
    title: "Ibrida",
    icon: Leaf,
    why:
      "Full o mild hybrid: consumi bassi in città e nessuna ansia da ricarica. Il miglior compromesso per un uso misto.",
  },
  {
    value: "electric",
    title: "Elettrica",
    icon: Zap,
    why:
      "Massima efficienza in città, zero emissioni locali, ingresso libero nelle ZTL. Serve però un punto di ricarica accessibile.",
  },
  {
    value: "lpg",
    title: "GPL",
    icon: Wind,
    why:
      "Costo per km molto basso e accesso a molte ZTL. Ottima scelta economica se percorri tanti chilometri con budget contenuto.",
  },
  {
    value: "any",
    title: "Indifferente",
    icon: Wrench,
    why:
      "Lasci scegliere all'AI l'alimentazione più adatta al tuo profilo d'uso e al chilometraggio annuo.",
  },
];

export const BRAND_TIERS = [
  {
    value: "utilitaria",
    title: "Utilitaria / Mainstream",
    icon: Car,
    why:
      "Fiat, Renault, Peugeot, Ford, Toyota, Hyundai… Prezzo accessibile, rete assistenza capillare, ricambi economici. La scelta razionale.",
  },
  {
    value: "premium",
    title: "Premium",
    icon: BadgeCheck,
    why:
      "Audi, BMW, Mercedes, Volvo, Alfa Romeo. Materiali migliori, guida più raffinata e valore residuo alto. Costi di gestione più elevati.",
  },
  {
    value: "luxury",
    title: "Luxury",
    icon: Crown,
    why:
      "Porsche, Maserati, Range Rover, Jaguar… Esclusività, prestazioni e comfort al top. Solo se il budget lo consente senza compromessi.",
  },
];

// Km/year ranges — value = representative km used for backend request
export const ANNUAL_KM_OPTIONS = [
  {
    value: 6000,
    title: "Meno di 10.000 km/anno",
    icon: MapPin,
    why:
      "Uso occasionale: seconda auto, city car o auto del weekend. Motorizzazioni benzina o elettriche sono le più efficienti a questi chilometraggi.",
  },
  {
    value: 15000,
    title: "10.000 – 20.000 km/anno",
    icon: Route,
    why:
      "Uso quotidiano tipico: casa-lavoro + weekend. Ideali ibride full/mild, elettriche con ricarica domestica, o benzina se pochi km cittadini.",
  },
  {
    value: 25000,
    title: "20.000 – 30.000 km/anno",
    icon: Timer,
    why:
      "Uso intensivo: molti spostamenti extraurbani o pendolari lunghi. Diesel moderni, plug-in hybrid o elettriche a lungo raggio diventano molto convenienti.",
  },
  {
    value: 40000,
    title: "Oltre 30.000 km/anno",
    icon: Plane,
    why:
      "Percorrenze molto alte, tipiche di rappresentanti o forti pendolari autostradali. Diesel efficienti o BEV con autonomia > 500 km e ricarica veloce.",
  },
];

// Budget ranges — value = representative budget used for backend request
export const BUDGET_OPTIONS = [
  {
    value: 3500,
    title: "Fino a 5.000 €",
    icon: Coins,
    why:
      "Solo usato: auto con qualche anno, chilometraggio più alto o segmento A. Perfetto come prima auto di appoggio o per neopatentati con budget ridotto.",
  },
  {
    value: 7500,
    title: "5.000 – 10.000 €",
    icon: Coins,
    why:
      "Usato di segmento B (Panda, Punto, Clio, Polo) in buone condizioni. Buon compromesso tra affidabilità e prezzo.",
  },
  {
    value: 12500,
    title: "10.000 – 15.000 €",
    icon: Wallet,
    why:
      "Usato recente di segmento B/C o entry-level nuovo (Panda, Sandero, Twingo). Ancora possibilità di garanzia costruttore.",
  },
  {
    value: 22000,
    title: "15.000 – 30.000 €",
    icon: Wallet,
    why:
      "La fascia più venduta in Italia: nuovo di segmento B/C, SUV compatti mainstream, ottime ibride. Massima varietà di scelta.",
  },
  {
    value: 42000,
    title: "30.000 – 55.000 €",
    icon: PiggyBank,
    why:
      "Premium accessibile: Audi Q3, BMW Serie 1/2, Volvo XC40, o SUV medi ben accessoriati e plug-in hybrid ricchi di tecnologia.",
  },
  {
    value: 75000,
    title: "55.000 – 100.000 €",
    icon: Gem,
    why:
      "Premium alto e sportive: BMW Serie 5, Mercedes GLE, Porsche Macan. Configurazioni ricche e motori più potenti.",
  },
  {
    value: 130000,
    title: "Oltre 100.000 €",
    icon: Crown,
    why:
      "Luxury e supersportive: Porsche 911, Range Rover, Maserati, Bentley. Zero compromessi su comfort, prestazioni e materiali.",
  },
];
